import { useParams, useNavigate } from "react-router-dom";
import { COMPANY_CONFIGS } from "@/config/companies";
import { useInnoventureTestDetails } from "@/hooks/useInnoventureTestDetails";
import { Card } from "@/components/ui/card";
import { InnoventureTestHeader } from "@/components/company-portal/InnoventureTestHeader";
import { InnoventureTestBody } from "@/components/company-portal/InnoventureTestBody";
import { InnoventureTestActions } from "@/components/company-portal/InnoventureTestActions";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function InnoventureTestDetails() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const config = COMPANY_CONFIGS.innoventure;

  const { data, isLoading, error } = useInnoventureTestDetails(testId);

  const handleBack = () => navigate('/innoventure-app');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading test details...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Test not found or you do not have access to this test.'}
            </AlertDescription>
          </Alert>
          <button
            onClick={handleBack}
            className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to Tests
          </button>
        </div>
      </div>
    );
  }

  const { 
    test, 
    questionCount, 
    hasAlreadyTaken,
    canResume,
    resumeFromQuestionIndex,
    isScheduled, 
    hasStarted, 
    hasEnded 
  } = data;

  return (
    <main className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto py-6 px-6">
        <Card className="overflow-hidden shadow-lg">
          <InnoventureTestHeader
            title={test.title}
            jobTitle={test.job_title || undefined}
            invitationOnly={test.invitation_only}
            questionCount={questionCount}
            timeLimit={test.time_limit || undefined}
            timeLimitType={test.time_limit_type || undefined}
            totalTimeLimit={test.total_time_limit || undefined}
            totalQuestionTimeSeconds={test.total_question_time_seconds || undefined}
            scheduledStartTime={test.scheduled_start_time || undefined}
            scheduledEndTime={test.scheduled_end_time || undefined}
            onBack={handleBack}
            accentColor={config.branding.buttonColor!}
            gradientStart={config.branding.gradientStart}
            gradientEnd={config.branding.gradientEnd}
            logo={config.branding.logo}
          />
          
          <InnoventureTestBody
            description={test.description}
            welcomeMessage={test.welcome_message || undefined}
          />
          
          <InnoventureTestActions
            testId={test.id}
            shareToken={test.share_token}
            hasAlreadyTaken={hasAlreadyTaken}
            canResume={canResume}
            resumeFromQuestionIndex={resumeFromQuestionIndex}
            isScheduledTest={isScheduled}
            hasStarted={hasStarted}
            hasEnded={hasEnded}
            scheduledStartTime={test.scheduled_start_time || undefined}
            onBack={handleBack}
            accentColor={config.branding.buttonColor!}
          />
        </Card>
      </div>
    </main>
  );
}
